/**
 * Отметка «доставка не состоялась».
 *
 * Причина обязательна: без неё заказ закрывается молча, и разобраться, почему
 * клиент не получил товар, потом уже не по чему. Сервер проверяет это тоже —
 * пустая причина отклоняется на эндпоинте.
 */
import { useState } from 'react';
import { FAIL_REASONS, type CurrentUser, type FailReason, type Order } from '@arbuz/shared';
import { ApiError, api } from '../api';
import { makeT } from '../i18n';
import { haptic } from '../tg';
import { inputStyle, labelStyle } from './courierUi';

interface Props {
  user: CurrentUser;
  order: Order;
  onDone: () => void;
}

export function CourierFail({ user, order, onDone }: Props) {
  const t = makeT(user.locale);
  const [reason, setReason] = useState<FailReason>('no_answer');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    haptic('warning');
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/courier/orders/${order.id}/fail`, {
        reason,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('actionFailed'));
      setBusy(false);
    }
  }

  return (
    <main className="page">
      <h1 className="h1">{t('failTitle')}</h1>
      <p className="muted" style={{ margin: '0 0 var(--s-4)' }}>
        {t('courierOrder')} #{order.public_number}
      </p>

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={labelStyle}>{t('failReasonLabel')}</legend>

        <div style={{ display: 'grid', gap: 'var(--s-2)' }}>
          {FAIL_REASONS.map((r) => (
            <label
              key={r}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--s-3)',
                minHeight: 'var(--tap-min)',
                padding: '0 var(--s-3)',
                borderRadius: 'var(--r-md)',
                border: `1px solid ${reason === r ? 'var(--c-primary-text)' : 'var(--c-border-input)'}`,
                background: reason === r ? 'var(--c-primary-subtle)' : 'var(--c-surface)',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="fail-reason"
                value={r}
                checked={reason === r}
                onChange={() => setReason(r)}
              />
              <span>{t(`failReason_${r}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div style={{ marginTop: 'var(--s-4)' }}>
        <label htmlFor="fail-comment" style={labelStyle}>
          {t('failCommentLabel')}
        </label>
        <textarea
          id="fail-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={200}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      {error && (
        <p
          role="alert"
          style={{ color: 'var(--c-danger)', fontSize: 'var(--fs-sm)', marginTop: 'var(--s-3)' }}
        >
          {error}
        </p>
      )}

      <button
        type="button"
        className="btn-primary"
        style={{ width: '100%', marginTop: 'var(--s-4)' }}
        disabled={busy}
        data-loading={busy ? 'true' : undefined}
        onClick={() => void submit()}
      >
        {t('confirmFail')}
      </button>
    </main>
  );
}
